import {
  LocationOnOutlined,
  WorkOutlineOutlined,
  LoginOutlined,
  PersonAddOutlined,
  PersonRemoveOutlined,
} from "@mui/icons-material";
import { Box, Typography, Divider, useTheme, Button, IconButton } from "@mui/material";
import UserImage from "components/UserImage";
import Friend from "components/Friend";
import FlexBetween from "components/FlexBetween";
import WidgetWrapper from "components/WidgetWrapper";
import { useSelector, useDispatch } from "react-redux";
import { setFriends } from "state";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../config";

const UserWidget = ({ userId, picturePath, isAuth, showLoginPrompt = false, userData = null }) => {
  const [user, setUser] = useState(userData);
  const { palette } = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);
  const loggedInUser = useSelector((state) => state.user);
  const loggedInUserId = loggedInUser?._id;
  const loggedInUserFriends = useSelector((state) => state.user?.friends || []);
  const dark = palette.neutral.dark;
  const medium = palette.neutral.medium;
  const main = palette.neutral.main;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  // Use friends from fetched user data (works for logged out users)
  // For friend button, check logged-in user's friends
  const isFriend = Array.isArray(loggedInUserFriends) && loggedInUserFriends.find((friend) => friend._id === userId);
  const isOwnProfile = loggedInUserId === userId;

  const getUser = async () => {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${BASE_URL}/users/${userId}`, {
      method: "GET",
      headers: headers,
    });
    const data = await response.json();
    setUser(data);
  };

  const patchFriend = async () => {
    if (!loggedInUserId || !token) return;

    const response = await fetch(
      `${BASE_URL}/users/${loggedInUserId}/${userId}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    const data = await response.json();
    dispatch(setFriends({ friends: data }));

    // Re-fetch the viewed user's data to update their friends list
    await getUser();
  };

  useEffect(() => {
    if (userId && !userData) {
      getUser();
    }
  }, [userId, userData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch user data when own friends list changes (from friending/defriending on posts)
  useEffect(() => {
    if (userId && isOwnProfile && loggedInUserFriends) {
      getUser();
    }
  }, [loggedInUserFriends]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show login prompt for guest users only when showLoginPrompt is true (home page)
  if (!isAuth && showLoginPrompt) {
    return (
      <WidgetWrapper>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="1.5rem"
          p="0.5rem"
        >
          <LoginOutlined sx={{ fontSize: "3rem", color: palette.primary.main }} />
          <Typography
            variant="h4"
            color={dark}
            fontWeight="500"
            textAlign="center"
          >
            Welcome to Sociopedia
          </Typography>
          <Typography
            color={medium}
            textAlign="center"
            sx={{ mb: "0.5rem" }}
          >
            Login to create posts, comment, and connect with friends
          </Typography>
          <Button
            onClick={() => navigate("/")}
            sx={{
              width: "100%",
              color: palette.background.alt,
              backgroundColor: palette.primary.main,
              borderRadius: "3rem",
              padding: "0.75rem 2rem",
              "&:hover": {
                backgroundColor: palette.primary.light,
              },
            }}
          >
            Login / Sign Up
          </Button>
        </Box>
      </WidgetWrapper>
    );
  }

  if (!user) {
    return null;
  }

  const {
    firstName,
    lastName,
    location,
    occupation
  } = user;

  return (
    <WidgetWrapper>
      {/* FIRST ROW */}
      <FlexBetween
        gap="0.5rem"
        pb="1.1rem"
      >
        <FlexBetween gap="1rem" onClick={() => navigate(`/profile/${userId}`)}>
          <UserImage image={picturePath} />
          <Box>
            <Typography
              variant="h4"
              color={dark}
              fontWeight="500"
              sx={{
                "&:hover": {
                  color: palette.primary.light,
                  cursor: "pointer",
                },
              }}
            >
              {firstName} {lastName}
            </Typography>
            <Typography color={medium}>{user.friends?.length || 0} friends</Typography>
          </Box>
        </FlexBetween>
        {!isOwnProfile && loggedInUserId && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              patchFriend();
            }}
            sx={{ backgroundColor: primaryLight, p: "0.6rem" }}
          >
            {isFriend ? (
              <PersonRemoveOutlined sx={{ color: primaryDark }} />
            ) : (
              <PersonAddOutlined sx={{ color: primaryDark }} />
            )}
          </IconButton>
        )}
      </FlexBetween>

      <Divider />

      {/* SECOND ROW */}
      <Box p="1rem 0">
        <Box display="flex" alignItems="center" gap="1rem" mb="0.5rem">
          <LocationOnOutlined fontSize="large" sx={{ color: main }} />
          <Typography color={medium}>{location}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap="1rem">
          <WorkOutlineOutlined fontSize="large" sx={{ color: main }} />
          <Typography color={medium}>{occupation}</Typography>
        </Box>
      </Box>

      <Divider />

      {/* THIRD ROW - FRIEND LIST */}
      <Box p="1rem 0">
        <Typography
          color={dark}
          variant="h5"
          fontWeight="500"
          sx={{ mb: "1.5rem" }}
        >
          Friend List
        </Typography>
        <Box display="flex" flexDirection="column" gap="1rem">
          {Array.isArray(user.friends) && user.friends.length > 0 ? (
            user.friends.map((friend) => (
              <Friend
                key={friend._id}
                friendId={friend._id}
                name={`${friend.firstName} ${friend.lastName}`}
                subtitle={friend.occupation}
                userPicturePath={friend.picturePath}
                hideActions={!isOwnProfile}
              />
            ))
          ) : (
            <Typography color={medium} textAlign="center" sx={{ py: "1rem" }}>
              No friends yet
            </Typography>
          )}
        </Box>
      </Box>

      <Divider />

      {/* FOURTH ROW */}
      {/* <Box p="1rem 0">
        <Typography fontSize="1rem" color={main} fontWeight="500" mb="1rem">
          Social Profiles
        </Typography>

        <FlexBetween gap="1rem" mb="0.5rem">
          <FlexBetween gap="1rem">
            <img src="twitter.png" alt="twitter" />
            <Box>
              <Typography color={main} fontWeight="500">
                Twitter
              </Typography>
              <Typography color={medium}>Social Network</Typography>
            </Box>
          </FlexBetween>
          <EditOutlined sx={{ color: main }} />
        </FlexBetween>

        <FlexBetween gap="1rem">
          <FlexBetween gap="1rem">
            <img src="linkedin.png" alt="linkedin" />
            <Box>
              <Typography color={main} fontWeight="500">
                Linkedin
              </Typography>
              <Typography color={medium}>Network Platform</Typography>
            </Box>
          </FlexBetween>
          <EditOutlined sx={{ color: main }} />
        </FlexBetween>
      </Box> */}
    </WidgetWrapper>
  );
};

export default UserWidget;